import { MachineEtat, TRANSITION_CANCELED } from '@/features/domaine/machine-etats'
import { DemandeRattachementConfiguration } from './DemandeRattachementConfiguration'
import { TypesContextesAutorisesEnum } from '@/features/auth/context-app/types/ContextApp'
import type { DemandeRattachementResult } from './types'
import type {
  EntiteAdherentDTO,
  EntitePrestaireDTO,
  InscriptionResultat
} from '@/features/api/extranet/dto/Rattachements'
import { useDialog } from '@/features/interface/dialog'
import { SilentError } from '@/features/shared/types'
import { VITE_SUPPORT_TEL } from '@/features/interface/shared/constants.ts'

export function useDemandeRattachement() {

  function getEntiteAdresseHTML(entite?:  EntiteAdherentDTO | EntitePrestaireDTO) {
    if (!entite) return []

    let denomination = entite.denomination?.trim()
    if (denomination?.length > 38 && entite?.sigle) {
      denomination = entite.sigle
    }
    if (denomination) {
      denomination = `<strong>${denomination}</strong>`
    }
    const cpVille = `${entite.adresse?.codePostal ?? ''} ${entite.adresse?.commune ?? ''}`

    let result = [
      denomination,
      // entite.adresse.complement, ??
      entite.adresse?.voie ?? '',
      entite.adresse?.complement ?? '',
      cpVille,
    ].filter(s => s?.trim())

    if (result.length > 0) {
      result = [
        "<span style='text-decoration: underline'>Adresse du courrier</span> :",
        '',
        ...result]
    }

    return result
  }

  function getMessageSirenSiret(options: { resultat: InscriptionResultat, entite?: EntiteAdherentDTO | EntitePrestaireDTO }) {

    const lignesAdresse = getEntiteAdresseHTML(options.entite)

    switch (options.resultat) {
      case 'AdministrateurExistant':
        return [
          'Un administrateur a été nommé pour l’entreprise, il a été prévenu de votre demande de rattachement afin qu’il puisse la valider ou la refuser.',
          `Si l’administrateur ne peut pas intervenir dans un délai raisonnable, merci de contacter notre support téléphonique au <strong>${VITE_SUPPORT_TEL}</strong> (horaires disponibles dans Aide & Contact).`,
        ]
      case 'CourrierAGenerer':
        return ["Un courrier d'activation va être envoyé à votre entreprise.", '', ...lignesAdresse]
      case 'CourrierActif':
        return ["Un courrier d'activation a été envoyé à votre entreprise.", '', ...lignesAdresse]
      case 'InvitationNiveauEntrepriseDejaExistante':
        return "Une demande d'habilitation a déjà été notifiée à l'administrateur AKTO de votre entreprise."
      case 'InvitationNiveauEtablissementDejaExistante':
        return "Une demande d'habilitation a déjà été notifiée à l'administrateur AKTO de votre établissement."
      case 'HabilitationNiveauEntrepriseDejaExistante':
        return "Vous disposez déjà d'habilitations pour cette entreprise."
      case 'HabilitationNiveauEtablissementDejaExistante':
        return [
          "Vous disposez déja d'habilitations pour cette établissement.",
          '',
          "Pour une même entreprise, il n'est pas possible de disposer à la fois d'habilitation de niveau entreprise et établissement.",
        ]
      case 'EntitePrincipaleSansAdresse':
        return `Nous n'avons pas été en mesure de récupérer votre adresse auprès de 'INSEE,
                ce qui rend impossible l'envoi du code d'activation par courrier, merci de contacter
                notre support téléphonique au <strong>${VITE_SUPPORT_TEL}</strong> (horaires disponibles dans Aide & Contact).`
      default:
        return [
          `Le traitement s'est terminé avec le code ${options.resultat}.`,
          "Veuillez s'il vous plait, vous rapprocher de notre support AKTO.",
        ]
    }
  }

  function getMessageSandbox(resultat?: DemandeRattachementResult) {
    return [
      `<strong>Une erreur technique est survenue</strong>`,
      '',
      "Veuillez s'il vous plait, vous rapprocher de notre support AKTO.",
      '',
      `<strong>Information techniques : </strong>`,
      `<pre style="white-space:normal;">${JSON.stringify(resultat)}</pre>`,
    ]
  }

  async function showDialog() {
    const engine = new MachineEtat(DemandeRattachementConfiguration)
    const result = await engine.launch({
      titre: 'Se rattacher à une entreprise ou un établissement',
      contextCourant: TypesContextesAutorisesEnum.Aucun,
      launchParams: undefined,
    }).catch(err => {
      if (err === TRANSITION_CANCELED) return
      if (err instanceof SilentError) return
      console.error(err)
    })
    if (!result) return

    const dialog = useDialog()
    if (result.modeRattachement === 'siren-siret' && result.demandeRattachementTraitée) {
      dialog.showDialog({
        title: 'Demande de rattachement terminée',
        message: getMessageSirenSiret({
          resultat: result.demandeRattachementTraitée,
          entite: result.adresseEntite
        }),
        isHtml: true,
        type: 'Ok',
      })
    } else if (result.modeRattachement === 'code' && result.codeEstValidé) {
      await dialog.showDialog({
        title: 'Votre rattachement a été activé ',
        message: 'Le rattachement a été pris en compte et vos habilitations vont être rechargées.',
        type: 'Ok',
      })
      window.location.reload()
    } else {
      // SANSBOX
      dialog.showDialog({
        title: 'Votre rattachement a échoué ',
        message: getMessageSandbox(result),
        type: 'Ok',
        isHtml: true,
      })
    }
  }

  return {
    showDialog,
  }
}
